import React from "react";
import { Link } from "react-router-dom";
import { Clapperboard, ArrowLeft } from "lucide-react";

/**
 * Public Terms & Conditions. Covers subscription, SMS/email provisions
 * (including the required "Message and data rates may apply" line), and
 * standard SaaS liability language. Plain, static, no auth required.
 */
export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-[#0D0F12] text-gray-200">
      <header className="border-b border-[#28303F] bg-[#14181F]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white" data-testid="terms-logo-link">
            <Clapperboard className="text-amber-400" size={20} />
            <span className="font-[Outfit] font-bold text-lg">Get2Set</span>
          </Link>
          <Link to="/login" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200">
            <ArrowLeft size={13} /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="terms-page">
        <h1 className="text-3xl font-extrabold font-[Outfit] text-white mb-2">Terms &amp; Conditions</h1>
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-8">Last updated: February 2026</p>

        <p className="mb-6 leading-relaxed">
          Get2Set is a service of Forced Perspective Productions LLC, which operates and offers it under the DBA "Get2Set" ("Get2Set", "we", "us", or "our").
          These Terms &amp; Conditions ("Terms") govern your access to and use of the Get2Set platform
          at <a className="text-amber-300 underline underline-offset-2" href="https://get2set.net">get2set.net</a> ("the Service").
          By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
        </p>

        <Section title="1. The Service">
          <p>
            Get2Set is a Software-as-a-Service platform that lets film and television productions create, publish, and distribute call sheets to their crew via email and SMS, with delivery and RSVP tracking.
          </p>
        </Section>

        <Section title="2. Accounts">
          <ul className="list-disc list-inside space-y-1">
            <li>You must be at least 18 years old to create an account.</li>
            <li>You are responsible for keeping your login credentials secure and for all activity on your account.</li>
            <li>You must provide accurate information and promptly update it when it changes.</li>
            <li>We may suspend or terminate accounts that violate these Terms.</li>
          </ul>
        </Section>

        <Section title="3. Subscription, free trial, and billing">
          <ul className="list-disc list-inside space-y-1">
            <li>Get2Set offers a <strong>7-day free trial</strong> that begins when you register.</li>
            <li>After the trial, continued access requires an active paid subscription: <strong>$24.99/month</strong> or <strong>$249/year</strong>.</li>
            <li>Subscriptions renew automatically at the end of each billing period. You may cancel at any time from the Billing page; cancellation takes effect at the end of the current period.</li>
            <li>All payments are processed by Stripe. We do not store full card numbers.</li>
            <li>Prices are in U.S. dollars and exclude applicable taxes. Taxes, when required, are calculated and collected by Stripe.</li>
            <li>Refunds are discretionary and generally not provided for partial billing periods.</li>
          </ul>
        </Section>

        <Section title="4. Email and SMS — your obligations as a sender">
          <p>When you use Get2Set to send email or SMS to crew, cast, clients, or other recipients, you represent and warrant that:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li>You have a prior, lawful relationship with each recipient and consent to send them transactional production-related messages.</li>
            <li>You will not use Get2Set to send marketing, promotional, political, or unsolicited bulk messages.</li>
            <li>You will promptly remove recipients who opt out (reply STOP for SMS, request unsubscribe for email) from your crew database and future sends.</li>
            <li>You will comply with all applicable laws, including the Telephone Consumer Protection Act (TCPA), CAN-SPAM, GDPR, CASL, and carrier A2P 10DLC rules.</li>
          </ul>
          <p className="mt-3">
            Get2Set may refuse, block, or terminate accounts that generate carrier complaints, high bounce rates, or appear to violate the above.
          </p>
        </Section>

        <Section title="5. Email and SMS — notice to recipients">
          <p>If you are receiving email or SMS from Get2Set on behalf of a production:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li>Messages are transactional and relate to a specific shoot you have been added to by a production coordinator.</li>
            <li><strong>Reply STOP to any SMS to opt out of SMS from that production. Reply HELP for help.</strong></li>
            <li>Message frequency varies by production. <strong>Message and data rates may apply from your carrier.</strong></li>
            <li>You may unsubscribe from email by replying to the sender or by emailing <a className="text-amber-300 underline underline-offset-2" href="mailto:support@get2set.net">support@get2set.net</a>.</li>
            <li>Get2Set does not use recipient phone numbers or emails for its own marketing and does not sell contact information.</li>
          </ul>
        </Section>

        <Section title="6. Acceptable use">
          <p>You may not, and may not allow anyone else to:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li>Use the Service to send spam, scam, or harassing messages.</li>
            <li>Impersonate another person or production.</li>
            <li>Attempt to probe, scan, or test the vulnerability of the Service or circumvent any security or authentication measures.</li>
            <li>Resell or sublicense access to the Service.</li>
            <li>Scrape, bulk-export, or data-mine the Service for competitive purposes.</li>
          </ul>
        </Section>

        <Section title="7. Content and intellectual property">
          <p>
            You retain all rights in call sheets, crew lists, logos, and other content you upload to Get2Set ("Your Content").
            You grant Get2Set a limited, non-exclusive license to store, process, display, and transmit Your Content strictly as needed to operate the Service for you (including rendering PDF call sheets, sending them via SendGrid and Twilio, and displaying them to recipients you authorize).
          </p>
          <p className="mt-3">
            The Get2Set name, logo, platform code, and underlying technology are owned by Forced Perspective Productions LLC and may not be used without written permission.
          </p>
        </Section>

        <Section title="8. Third-party services">
          <p>
            The Service relies on third-party providers including SendGrid, Twilio, Stripe, MongoDB Atlas, OpenStreetMap, Photon, Open-Meteo, and the U.S. National Weather Service.
            Their availability, pricing, and policies may change. We are not responsible for outages, errors, or deliverability issues caused by these providers.
          </p>
        </Section>

        <Section title="9. Disclaimers">
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY MESSAGE WILL BE DELIVERED.
          </p>
        </Section>

        <Section title="10. Limitation of liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, FORCED PERSPECTIVE PRODUCTIONS LLC AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
          </p>
        </Section>

        <Section title="11. Indemnification">
          <p>
            You agree to indemnify and hold harmless Forced Perspective Productions LLC, its affiliates, officers, employees, and agents from any claim or demand (including reasonable attorneys' fees) arising out of or related to Your Content, your use of the Service, or your violation of these Terms, including without limitation any claim relating to messages you sent or recipients you added without their consent.
          </p>
        </Section>

        <Section title="12. Termination">
          <p>
            You may cancel your account at any time from the Billing page. We may suspend or terminate accounts that violate these Terms or that we reasonably believe are being used to send unlawful or abusive messages. Upon termination, your right to use the Service ends and we may delete your data after a reasonable grace period.
          </p>
        </Section>

        <Section title="13. Governing law and disputes">
          <p>
            These Terms are governed by the laws of the State of California, without regard to its conflict-of-laws principles.
            Any dispute arising out of or relating to these Terms or the Service shall be resolved in the state or federal courts located in Los Angeles County, California, and the parties consent to the personal jurisdiction of those courts.
          </p>
        </Section>

        <Section title="14. Changes to these Terms">
          <p>
            We may update these Terms from time to time. Material changes will be announced in-app or by email to account holders. Your continued use of the Service after a change takes effect means you accept the updated Terms.
          </p>
        </Section>

        <Section title="15. Contact">
          <p>
            Forced Perspective Productions LLC d/b/a Get2Set<br />
            Email: <a className="text-amber-300 underline underline-offset-2" href="mailto:support@get2set.net">support@get2set.net</a>
          </p>
        </Section>

        <p className="mt-10 text-xs text-gray-500">
          See also our <Link to="/privacy" className="text-amber-300 underline underline-offset-2" data-testid="terms-privacy-link">Privacy Policy</Link>.
        </p>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6 leading-relaxed">
      <h2 className="text-lg font-semibold font-[Outfit] text-white mb-2">{title}</h2>
      <div className="text-sm text-gray-300 space-y-2">{children}</div>
    </section>
  );
}
